import React from 'react';
import { Typography, Link } from '@mui/material';

/**
 * Renders text with embedded link placeholders: {link|text|url}
 * Also supports paragraph breaks via double newlines.
 * 
 * @param text - Text containing optional {link|text|url} placeholders
 * @param variant - MUI Typography variant (default: 'body1')
 * @param sx - Optional MUI sx prop for Typography styling
 * @returns Array of Typography components with embedded links
 * 
 * @example
 * renderWithLinks('Check {link|our site|https://example.com} for more info.')
 */
export const renderWithLinks = (
  text: string,
  variant: 'body1' | 'body2' | 'caption' = 'body1',
  sx?: any
) => {
  if (!text) return null;

  // Support paragraph breaks by double newlines
  const paragraphs = text.split(/\n{2,}/);

  return paragraphs.map((para, pIdx) => {
    const parts: React.ReactNode[] = [];
    const regex = /\{link\|([^|}]+)\|([^}]+)\}/g; // {link|text|url}
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(para)) !== null) {
      const [full, linkText, url] = match;
      const start = match.index;
      if (start > lastIndex) {
        parts.push(para.slice(lastIndex, start));
      }
      parts.push(
        <Link
          key={`p${pIdx}-l${start}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
        >
          {linkText}
        </Link>
      );
      lastIndex = start + full.length;
    }
    if (lastIndex < para.length) {
      parts.push(para.slice(lastIndex));
    }

    return (
      <Typography key={`p${pIdx}`} variant={variant} sx={sx || { mb: 3 }}>
        {parts}
      </Typography>
    );
  });
};
